import { store } from '../app/store';
import { CallBarState } from '../constants/CallBarState';
import { SnackbarType } from '../constants/SnackbarType';
import { CallActivityStatusEnum } from '../Enums/CallActivityActionType';
import { showSnackbar } from '../slices/snackbar';
import { addCallingContact, clearCallingContact, clearMissedCallCallee } from '../slices/callingContact';
// import { deleteScheduleCall } from '../slices/scheduledCalls';
import { updateUserAvailabilityStatus } from '../slices/auth';
import { updateCallBarState, setIncomingCallIdle, updateCurrentActiveCallRecordId, setIsCurrentCallInHold } from '../slices/callCentre';
import BrowserApiService from './BrowserApi';
import SaleforceService from '../Services/SaleforceService';
import TwilioVoiceService from '../Services/TwilioVoiceService';
import CallNotificationService from '../Services/Call/CallNotification';
import { addBookmark } from '../slices/callTranscript';
import { appDebugLog, appDebugWarn } from '../Util/appDebugLog';
import { describeJwtToken, isJwtExpired } from '../Util/jwtUtils';
import { NetworkAvailability } from '../Util/NetworkAvailability';
import { resolveBeaconConnection } from './Beacon/resolveBeaconConnection';
import { onAccessTokenRefreshed } from './instances/VoiceIQAPI';
import { isVoiceTabLeader, onVoiceLeadershipChange, startVoiceTabLeader } from '../Util/voiceTabLeader';
import { publishBeaconMessage, startBeaconMessageRelay } from '../Util/beaconMessageRelay';
import { DefaultUserStatuses } from '../constants/UserStatusesEnum';

const LogTitle = 'RealTimeClient';

const BEACON_WS_REFRESH_SKEW_MS = 60_000;
/** Match resolveBeaconConnection gateway skew — only force /connect/refresh when JWT is actually stale. */
const GATEWAY_REFRESH_SKEW_MS = 20_000;
export default class RealTimeClient {
  static heartbeatIntervalRef: NodeJS.Timeout | null = null;
  static heartbeatTimeoutRef: number | null = null;
  static lastHeartbeat: Date | null = null;

  // The WebSocket connection object
  static connection: WebSocket | null = null;
  static connectionHubProxy = null;

  static _capturedBookmark: any[] = [];

  // The twilio verification success callback, will be called when the verification is successful
  static twilioVerificationSuccessCallback = null;

  // When connection drops, we queue messages until connection is re-established
  static pendingMessagesQueue: any[] = [];

  // Connection management
  static isConnecting = false;
  static isReconnecting = false;
  static reconnectAttempts = 0;
  static maxReconnectAttempts = 10;
  static baseReconnectDelay = 1000;
  static maxReconnectDelay = 30000;
  static retryTimeoutId: number | null = null;

  // Connection details
  static connectionUrl: string | null = null;
  static accessToken: string | null = null;

  /** Avoid stacking duplicate `visibilitychange` listeners if `connect` runs more than once. */
  static pageVisibilityListenerAttached = false;

  /** Serialize open/close/reconnect so visibility, backoff timer, and replace never race. */
  static socketOpChain: Promise<void> = Promise.resolve();

  /** After gateway token refresh, rotate Beacon WebSocket credentials if a session was established. */
  private static accessTokenRefreshUnsubscribe: (() => void) | null = null;

  /** Reconnect deferred until NIC / DNS recover after sleep or offline. */
  static reconnectPausedForOffline = false;
  private static networkResumeUnsubscribe: (() => void) | null = null;

  /** One tab owns this socket, the same tab that owns the phone line. */
  static beaconArmed = false;
  private static beaconRelayReady = false;
  private static beaconLeaderBound = false;
  private static beaconGeneration = 0;
  private static beaconConnectInFlight: Promise<void> | null = null;

  static ensureNetworkResumeListener() {
    if (RealTimeClient.networkResumeUnsubscribe) {
      return;
    }
    RealTimeClient.networkResumeUnsubscribe = NetworkAvailability.subscribe((online) => {
      if (!online || !RealTimeClient.reconnectPausedForOffline) {
        return;
      }
      if (!isVoiceTabLeader()) {
        RealTimeClient.reconnectPausedForOffline = false;
        return;
      }
      if (!RealTimeClient.connectionUrl && !RealTimeClient.accessToken) {
        RealTimeClient.reconnectPausedForOffline = false;
        return;
      }
      RealTimeClient.reconnectPausedForOffline = false;
      appDebugLog(LogTitle, 'Network restored — resuming Beacon WebSocket reconnect');
      RealTimeClient.scheduleReconnect();
    });
  }

  static pauseReconnectUntilOnline(reason: string) {
    this.clearPendingReconnectTimer();
    this.isReconnecting = false;
    this.reconnectPausedForOffline = true;
    this.ensureNetworkResumeListener();
    appDebugWarn(LogTitle, `Reconnect paused until network is online (${reason})`);
  }

  static ensureAccessTokenRefreshListener() {
    if (RealTimeClient.accessTokenRefreshUnsubscribe) {
      return;
    }
    RealTimeClient.accessTokenRefreshUnsubscribe = onAccessTokenRefreshed(() => {
      if (!isVoiceTabLeader() || !RealTimeClient.connectionUrl) {
        return;
      }

      if (!NetworkAvailability.isOnline()) {
        RealTimeClient.pauseReconnectUntilOnline('gateway token refreshed while offline');
        return;
      }

      const connectionState = RealTimeClient.getConnectionState();
      const wsTokenStale = isJwtExpired(RealTimeClient.accessToken, BEACON_WS_REFRESH_SKEW_MS);

      // Proactive gateway refresh keeps REST working; WebSocket JWT is a separate 60m token.
      // Reconnect only when the socket is down or the WS JWT is expired/near expiry.
      if (connectionState === 'Connected' && !wsTokenStale) {
        appDebugLog(
          '[WebSocketAuth] Gateway Bearer refreshed (REST only); WebSocket still OK — skip reconnect',
          { connectionState }
        );
        return;
      }

      appDebugLog('[WebSocketAuth] Gateway Bearer refreshed; reconnecting Beacon WebSocket', {
        connectionState,
        wsTokenStale,
        reason: wsTokenStale ? 'ws-jwt-stale' : 'socket-not-connected',
      });
      void RealTimeClient.reconnectAfterGatewayTokenRefresh();
    });
  }

  static stillOwnsBeacon(generation: number): boolean {
    return generation === this.beaconGeneration && this.beaconArmed && isVoiceTabLeader();
  }

  /**
   * Open the Beacon WebSocket on the phone-line owner only.
   * Other tabs wait; they do not call /api/v1/beacon/connect.
   */
  static armBeacon() {
    this.beaconArmed = true;
    this.ensureBeaconLeader();
    if (isVoiceTabLeader()) {
      void this.connectAsLeader();
    }
  }

  static ensureBeaconRelay() {
    if (this.beaconRelayReady || typeof window === 'undefined') {
      return;
    }
    this.beaconRelayReady = true;
    startBeaconMessageRelay((type, data) => {
      if (isVoiceTabLeader()) {
        return;
      }
      this.handleWebSocketMessage(type, data, true);
    });
  }

  /** Forward a socket message so other tabs paint the same UI. Heartbeats stay local. */
  static relayBeaconMessage(type: string, data: unknown) {
    if (!isVoiceTabLeader()) {
      return;
    }
    if (type === 'HeartbeatResponse' || type === 'AvailabilityInquire' || type === 'RCTranscription') {
      return;
    }
    publishBeaconMessage(type, data);
  }

  static ensureBeaconLeader() {
    this.ensureBeaconRelay();
    if (this.beaconLeaderBound || typeof window === 'undefined') {
      return;
    }
    this.beaconLeaderBound = true;
    onVoiceLeadershipChange((isLeader) => {
      if (!this.beaconArmed) {
        return;
      }
      if (isLeader) {
        appDebugLog(LogTitle, 'This tab owns the Beacon WebSocket');
        void this.connectAsLeader();
      } else {
        appDebugLog(LogTitle, 'Beacon WebSocket released — another tab owns the line');
        this.releaseBeaconSocket();
      }
    });
    startVoiceTabLeader();
  }

  static connectAsLeader(): Promise<void> {
    if (!this.stillOwnsBeacon(this.beaconGeneration)) {
      return Promise.resolve();
    }
    const state = this.getConnectionState();
    if (state === 'Connected' || state === 'Connecting') {
      return Promise.resolve();
    }
    if (this.beaconConnectInFlight) {
      return this.beaconConnectInFlight;
    }

    const generation = this.beaconGeneration;
    const run = this.openBeaconSocket(generation).finally(() => {
      if (this.beaconConnectInFlight === run) {
        this.beaconConnectInFlight = null;
      }
    });
    this.beaconConnectInFlight = run;
    return run;
  }

  static async openBeaconSocket(generation: number) {
    try {
      const fresh = await resolveBeaconConnection();
      if (!this.stillOwnsBeacon(generation)) {
        return;
      }
      await this.connect(fresh.connectionUrl, fresh.accessToken);
    } catch (error) {
      if (!this.stillOwnsBeacon(generation)) {
        return;
      }
      appDebugWarn(LogTitle, 'Beacon connect failed', { error });
      this.scheduleReconnect();
    }
  }

  /** Drop the socket without letting reconnect run on a tab that no longer owns the line. */
  static releaseBeaconSocket() {
    this.beaconGeneration += 1;
    this.beaconConnectInFlight = null;
    this.clearPendingReconnectTimer();
    this.isReconnecting = false;
    this.reconnectPausedForOffline = false;
    this.stopApplicationHeartbeat();
    this.connectionUrl = null;
    this.accessToken = null;

    const ws = this.connection;
    this.connection = null;
    if (!ws) {
      return;
    }
    ws.onopen = null;
    ws.onmessage = null;
    ws.onclose = null;
    ws.onerror = null;
    try {
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close(1000, 'another tab owns beacon');
      }
    } catch {
      // ignore
    }
  }

  /**
   * Creates a WebSocket connection to the server.
   * @param connectionUrl connection URL
   * @param accessToken authentication token
   * @returns The connection object.
   */
  static async connect(connectionUrl: string, accessToken: string) {
    if (!isVoiceTabLeader()) {
      appDebugLog(LogTitle, 'Beacon WebSocket skipped — this tab does not own the line');
      return null;
    }
    const generation = this.beaconGeneration;
    this.beaconArmed = true;
    this._capturedBookmark = [];
    this.ensureAccessTokenRefreshListener();
    this.connectionUrl = connectionUrl;
    this.accessToken = accessToken;

    await this.createWebSocketConnection(connectionUrl, accessToken);
    if (!this.stillOwnsBeacon(generation) || !RealTimeClient.connection) {
      return null;
    }
    
    // Set up all event handlers
    this.updateUserNotifications();
    this.UpdateContactInfo();
    this.updateCallStatusChange();
    this.recordingAdded();
    this.transcriptionAdded();
    this.bookmarkTranscriptionsAdded();
    this.availabilityInquireEvent();
    this.updateUserStatusChange();
    this.updateUserDisconnectMessage();
    this.RCCallTranscript();
    this.RPBookmarkTriggered();
    this.registerPageVisibilityHandlers();
    
    // If connection succeeds, let's send all pending messages
    this.sendPendingMessages();

    return RealTimeClient.connection;
  }

  /** Run socket work one-at-a-time (connect, reconnect timer, visibility reconnect, cleanup). */
  static enqueueSocketOp<T>(fn: () => Promise<T>): Promise<T> {
    const run = RealTimeClient.socketOpChain.then(fn);
    RealTimeClient.socketOpChain = run.then(
      () => undefined,
      () => undefined
    );
    return run;
  }

  /**
   * Build WS URL and detach any existing socket (handlers cleared so onclose does not schedule reconnect).
   */
  static detachCurrentWebSocket = async (): Promise<void> => {
    const ws = RealTimeClient.connection;
    if (!ws) {
      return;
    }
    ws.onopen = null;
    ws.onmessage = null;
    ws.onclose = null;
    ws.onerror = null;
    try {
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close(1000, 'Client detach');
      }
    } catch {
      // ignore
    }
    if (RealTimeClient.connection === ws) {
      RealTimeClient.connection = null;
    }
  };

  static waitUntilWebSocketOpen = (ws: WebSocket, timeoutMs: number): Promise<void> => {
    if (ws.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      const timer = window.setTimeout(() => {
        ws.removeEventListener('open', onOpen);
        ws.removeEventListener('error', onErr);
        ws.removeEventListener('close', onClose);
        try {
          if (ws.readyState === WebSocket.CONNECTING) {
            ws.close();
          }
        } catch {
          // ignore
        }
        reject(new Error('WebSocket connection timeout'));
      }, timeoutMs);

      const cleanup = () => {
        clearTimeout(timer);
        ws.removeEventListener('open', onOpen);
        ws.removeEventListener('error', onErr);
        ws.removeEventListener('close', onClose);
      };

      const onOpen = () => {
        cleanup();
        resolve();
      };
      const onErr = () => {
        cleanup();
        reject(new Error('WebSocket connection error'));
      };
      const onClose = () => {
        cleanup();
        reject(new Error('WebSocket closed before open'));
      };

      ws.addEventListener('open', onOpen, { once: true });
      ws.addEventListener('error', onErr, { once: true });
      ws.addEventListener('close', onClose, { once: true });
    });
  };

  /**
   * Lifecycle handlers only (open handshake is handled before this runs).
   */
  static setupWebSocketEventHandlers = (ws: WebSocket) => {
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        appDebugLog(LogTitle, 'WebSocket message received:', data);
        const dataEvent = data.OperationName || data.operationName || data.event;
        const payload = data.data ?? data.Data ?? {};

        if (dataEvent) {
          this.handleWebSocketMessage(dataEvent, payload);
          this.relayBeaconMessage(dataEvent, payload);
        }
      } catch (parseError) {
        console.error(LogTitle, 'Error parsing WebSocket message:', parseError);
      }
    };

    ws.onclose = (event) => {
      const closeReason = event.reason || 'Connection closed';
      const closeCode = event.code;
      appDebugLog(LogTitle, 'WebSocket connection closed:', closeReason, 'Code:', closeCode);
      this.stopApplicationHeartbeat();

      if (RealTimeClient.connection === ws) {
        RealTimeClient.connection = null;
      }

      if (!this.connectionUrl || !this.accessToken) {
        return;
      }

      if (closeCode === 1000) {
        return;
      }

      if (!isVoiceTabLeader()) {
        return;
      }

      this.scheduleReconnect();
    };

    ws.onerror = (error) => {
      console.error(LogTitle, 'WebSocket error:', error);
    };
  };

  static createWebSocketConnectionLocked = async (
    connectionUrl: string,
    accessToken: string,
    options?: { forceFreshCredentials?: boolean }
  ): Promise<void> => {
    const generation = this.beaconGeneration;
    if (!this.stillOwnsBeacon(generation)) {
      return;
    }

    this.clearPendingReconnectTimer();
    this.ensureAccessTokenRefreshListener();

    let url = connectionUrl;
    let token = accessToken;

    if (isJwtExpired(token, 0)) {
      appDebugWarn(
        '[WebSocketAuth] Cached Beacon WebSocket JWT is EXPIRED (connect would get HTTP 401)',
        describeJwtToken(token, 'cachedWsJwt')
      );
    }

    if (
      options?.forceFreshCredentials ||
      isJwtExpired(token, BEACON_WS_REFRESH_SKEW_MS)
    ) {
      appDebugLog('[WebSocketAuth] Fetching fresh credentials', {
        forceFreshCredentials: options?.forceFreshCredentials ?? false,
      });
      const fresh = await resolveBeaconConnection({
        forceGatewayRefresh: options?.forceFreshCredentials ?? false,
      });
      if (!this.stillOwnsBeacon(generation)) {
        return;
      }
      url = fresh.connectionUrl;
      token = fresh.accessToken;
    }

    if (!this.stillOwnsBeacon(generation)) {
      return;
    }

    appDebugLog('[WebSocketAuth] Opening WebSocket /connect', describeJwtToken(token, 'wsJwtForConnect'));

    this.connectionUrl = url;
    this.accessToken = token;

    const wsUrl = url.includes('?')
      ? `${url}&access_token=${encodeURIComponent(token)}`
      : `${url}?access_token=${encodeURIComponent(token)}`;

    await this.detachCurrentWebSocket();
    if (!this.stillOwnsBeacon(generation)) {
      return;
    }

    const ws = new WebSocket(wsUrl);
    if (!this.stillOwnsBeacon(generation)) {
      try {
        ws.close(1000, 'another tab owns beacon');
      } catch {
        // ignore
      }
      return;
    }
    RealTimeClient.connection = ws;

    try {
      await this.waitUntilWebSocketOpen(ws, 10000);
    } catch (error) {
      if (RealTimeClient.connection === ws) {
        RealTimeClient.connection = null;
      }
      try {
        ws.close();
      } catch {
        // ignore
      }
      if (!this.stillOwnsBeacon(generation)) {
        return;
      }
      // Keep real connect failures visible — these are actionable.
      console.error('[WebSocketAuth] WebSocket connect failed', {
        message: (error as Error)?.message,
        likelyHttp401: isJwtExpired(token, 0),
      });
      throw error;
    }

    if (!this.stillOwnsBeacon(generation) || RealTimeClient.connection !== ws) {
      if (RealTimeClient.connection === ws) {
        RealTimeClient.connection = null;
      }
      try {
        ws.close(1000, 'another tab owns beacon');
      } catch {
        // ignore
      }
      return;
    }

    this.setupWebSocketEventHandlers(ws);
    appDebugLog(LogTitle, 'WebSocket connection established successfully');
    TwilioVoiceService.syncShownAvailabilityToRedis('beacon-connected');
    this.isReconnecting = false;
    this.reconnectAttempts = 0;
    this.startApplicationHeartbeat();
  };

  /**
   * Create a WebSocket connection (queued — never concurrent with reconnect or cleanup).
   */
  static createWebSocketConnection = (
    connectionUrl: string,
    accessToken: string
  ): Promise<void> => {
    return this.enqueueSocketOp(() =>
      this.createWebSocketConnectionLocked(connectionUrl, accessToken)
    );
  };

  static sendApplicationHeartbeat = () => {
    const timestamp = new Date().toISOString();

    const heartbeatMessage = {
        OperationName: 'ClientHeartbeat',
        Timestamp: timestamp,
        ClientInfo: {}
    };

    this.sendMessage(heartbeatMessage);
  };

  static startApplicationHeartbeat = () => {
    // Send initial heartbeat
    this.sendApplicationHeartbeat();
    
    // Set up periodic heartbeat every 30 seconds
    this.heartbeatIntervalRef = setInterval(() => {
        this.sendApplicationHeartbeat();
    }, 30000); // 30 seconds interval
    
    // Set up heartbeat timeout detection
    this.resetHeartbeatTimeout();
  };

  static stopApplicationHeartbeat = () => {
    if (this.heartbeatIntervalRef) {
        clearInterval(this.heartbeatIntervalRef);
        this.heartbeatIntervalRef = null;
    }
    if (this.heartbeatTimeoutRef) {
        clearTimeout(this.heartbeatTimeoutRef);
        this.heartbeatTimeoutRef = null;
    }
  };

  static resetHeartbeatTimeout = () => {
    // Clear existing timeout
    if (this.heartbeatTimeoutRef) {
        clearTimeout(this.heartbeatTimeoutRef);
    }
    
    // Set new timeout - if no heartbeat response within 90 seconds, reconnect
    this.heartbeatTimeoutRef = window.setTimeout(() => {
        if (!isVoiceTabLeader()) {
          return;
        }
        if (!NetworkAvailability.isOnline()) {
          appDebugWarn('Heartbeat timeout while offline — pausing reconnect until network returns');
          this.stopApplicationHeartbeat();
          if (RealTimeClient.connection) {
            try {
              RealTimeClient.connection.close();
            } catch {
              // ignore
            }
          }
          this.pauseReconnectUntilOnline('heartbeat timeout offline');
          return;
        }

        appDebugWarn('Heartbeat timeout - server not responding, attempting to reconnect');
    
        this.stopApplicationHeartbeat();
        
        // Close current connection and let the onclose handler trigger reconnection 
        if (RealTimeClient.connection) {
            RealTimeClient.connection.close();
        }
    }, 90000);
    
  };

  /**
   * Handle incoming WebSocket messages
   */
  static handleWebSocketMessage = (type: string, data: any, relayed = false) => {
    switch (type) {
      case 'UserNotification':
        this.handleUserNotification(data, relayed);
        break;
      case 'UpdateContactInfo':
        this.handleUpdateContactInfo(data);
        break;
      case 'CallStatusChanged':
        this.handleCallStatusChanged(data, relayed);
        break;
      case 'RCTranscription':
        if (!relayed) this.handleRCTranscription(data);
        break;
      // case 'RPBookmarkTriggered':
      //   this.handleRPBookmarkTriggered(data, relayed);
      //   break;
      case 'AvailabilityInquire':
        if (!relayed) this.handleAvailabilityInquire();
        break;
      case 'UserStatusUpdate':
        this.handleUserStatusUpdate(data);
        break;
      case 'UserDisconnectMessage':
        this.handleUserDisconnectMessage(data);
        break;
      case 'HeartbeatResponse':
          // Server acknowledged our heartbeat
          this.lastHeartbeat = new Date();
          this.resetHeartbeatTimeout();
        break;
      default:
        appDebugLog(LogTitle, 'Unknown message type:', type, data);
    }
  }

  /**
   * Schedule reconnection with exponential backoff
   */
  static clearPendingReconnectTimer = () => {
    if (this.retryTimeoutId !== null) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }
  };

  static scheduleReconnect = () => {
    if (!isVoiceTabLeader()) {
      return;
    }
    if (this.isReconnecting) {
      appDebugLog(LogTitle, 'Reconnect already scheduled; skipping duplicate');
      return;
    }

    if (!NetworkAvailability.isOnline()) {
      this.pauseReconnectUntilOnline('offline');
      return;
    }

    this.reconnectPausedForOffline = false;
    this.isReconnecting = true;
    this.reconnectAttempts++;

    const cappedAttempt = Math.min(this.reconnectAttempts, this.maxReconnectAttempts);
    const delay = Math.min(
      this.baseReconnectDelay * Math.pow(2, cappedAttempt - 1) + Math.random() * 1000,
      this.maxReconnectDelay
    );

    appDebugLog(
      LogTitle,
      `Scheduling reconnect attempt ${this.reconnectAttempts} in ${delay}ms`
    );

    this.retryTimeoutId = window.setTimeout(async () => {
      this.retryTimeoutId = null;
      try {
        if (!isVoiceTabLeader()) {
          this.isReconnecting = false;
          return;
        }
        if (this.getConnectionState() === 'Connected') {
          this.isReconnecting = false;
          return;
        }
        if (!NetworkAvailability.isOnline()) {
          this.pauseReconnectUntilOnline('went offline before attempt');
          return;
        }
        appDebugLog('[WebSocketAuth] Reconnect: restoring Beacon WebSocket');
        await this.reconnectWithFreshBeaconCredentials();
        this.isReconnecting = false;
      } catch (error) {
        this.isReconnecting = false;
        if (
          NetworkAvailability.isNetworkError(error) ||
          !NetworkAvailability.isOnline()
        ) {
          this.pauseReconnectUntilOnline('network error');
          return;
        }
        // Non-network reconnect failures only — avoid spam on sleep/DNS drops.
        console.error(LogTitle, 'Reconnect failed:', (error as Error)?.message ?? error);
        this.scheduleReconnect();
      }
    }, delay) as number;
  };

  /**
   * Disconnect the socket
   */
  static disconnectAsync = async (): Promise<void> => {
    await this.detachCurrentWebSocket();
  };

  static safeDisconnect = async (): Promise<void> => {
    if (!RealTimeClient.connection) {
      appDebugLog(LogTitle, 'Connection not set. Skipping real time client disconnect');
      return;
    }
    await this.detachCurrentWebSocket();
  };

  /**
   * Cleanup all resources and disconnect
   */
  static cleanup = async () => {
    this.beaconArmed = false;
    this.beaconGeneration += 1;
    this.beaconConnectInFlight = null;
    await this.enqueueSocketOp(async () => {
      appDebugLog(LogTitle, 'Cleaning up RealTimeClient resources');

      this.clearPendingReconnectTimer();
      this.stopApplicationHeartbeat();

      this.isReconnecting = false;
      this.reconnectAttempts = 0;
      this.reconnectPausedForOffline = false;
      this.pendingMessagesQueue = [];

      this.connectionUrl = null;
      this.accessToken = null;

      if (RealTimeClient.accessTokenRefreshUnsubscribe) {
        RealTimeClient.accessTokenRefreshUnsubscribe();
        RealTimeClient.accessTokenRefreshUnsubscribe = null;
      }
      if (RealTimeClient.networkResumeUnsubscribe) {
        RealTimeClient.networkResumeUnsubscribe();
        RealTimeClient.networkResumeUnsubscribe = null;
      }

      await this.detachCurrentWebSocket();
      this.unregisterPageVisibilityHandlers();

      appDebugLog(LogTitle, 'Cleanup complete');
    });
  };

  /**
   * After gateway Bearer refresh only: get a new WebSocket JWT (do not call /connect/refresh again).
   */
  static reconnectAfterGatewayTokenRefresh = (): Promise<void> => {
    if (!isVoiceTabLeader()) {
      return Promise.resolve();
    }
    return this.enqueueSocketOp(async () => {
      if (!isVoiceTabLeader()) {
        return;
      }
      const fresh = await resolveBeaconConnection({ gatewayAlreadyRefreshed: true });
      if (!isVoiceTabLeader()) {
        return;
      }
      await this.createWebSocketConnectionLocked(fresh.connectionUrl, fresh.accessToken);
    });
  };

  /**
   * Reconnect after disconnect/backoff.
   * Sleep/offline drops are network failures — do NOT force /connect/refresh (causes unauthorized storms).
   * Only refresh gateway / mint a new Beacon WS JWT when tokens are actually expired or missing.
   */
  static reconnectWithFreshBeaconCredentials = (): Promise<void> => {
    if (!isVoiceTabLeader()) {
      return Promise.resolve();
    }
    return this.enqueueSocketOp(async () => {
      if (!isVoiceTabLeader()) {
        return;
      }
      if (!NetworkAvailability.isOnline()) {
        throw new Error('Network unavailable — deferring Beacon reconnect');
      }

      const gatewayToken = store.getState()?.auth?.authUser?.AccessToken as string | undefined;
      const gatewayStale = !gatewayToken || isJwtExpired(gatewayToken, GATEWAY_REFRESH_SKEW_MS);
      const wsTokenUsable =
        !!this.accessToken && !isJwtExpired(this.accessToken, BEACON_WS_REFRESH_SKEW_MS);

      // Same sleep/wake path: reopen with cached URL + WS JWT when still valid.
      if (this.connectionUrl && wsTokenUsable && !gatewayStale) {
        appDebugLog('[WebSocketAuth] Reconnect with cached credentials (tokens still valid)');
        await this.createWebSocketConnectionLocked(this.connectionUrl, this.accessToken!);
        return;
      }

      appDebugLog('[WebSocketAuth] Reconnect needs credential mint', {
        gatewayStale,
        wsTokenUsable,
        hasCachedUrl: !!this.connectionUrl,
      });
      const fresh = await resolveBeaconConnection({ forceGatewayRefresh: gatewayStale });
      if (!isVoiceTabLeader()) {
        return;
      }
      await this.createWebSocketConnectionLocked(fresh.connectionUrl, fresh.accessToken);
    });
  };

  /**
   * Reconnect the client (refreshes credentials when the cached WebSocket JWT is stale).
   */
  static reconnectClient = (): Promise<void> => {
    if (!isVoiceTabLeader()) {
      return Promise.resolve();
    }
    if (!this.connectionUrl && !this.accessToken) {
      return Promise.resolve();
    }
    if (isJwtExpired(this.accessToken, BEACON_WS_REFRESH_SKEW_MS)) {
      return this.reconnectWithFreshBeaconCredentials();
    }
    if (!this.connectionUrl || !this.accessToken) {
      return this.reconnectWithFreshBeaconCredentials();
    }
    return this.createWebSocketConnection(this.connectionUrl, this.accessToken);
  };

  /**
   * Send the device connection status to the server. If the device is busy,
   * this user will not get another incoming till the device becomes idle
   */
  static sendActiveConnectionIds = async () => {
    const { userId, tenantCode } = store.getState()?.auth?.authUser;

    let callRecordId = null;
    const connection = TwilioVoiceService.getActiveConnection();

    if (connection) {
      const callDetails = TwilioVoiceService.getCallDetailsFromConnection(TwilioVoiceService.callConnection);
      callRecordId = (callDetails as any)?.callRecordId;
    }

    const currentConnectionState = this.getConnectionState();
    appDebugLog(LogTitle, "currentConnectionState", { tenantCode, userId, callRecordId });

    if (currentConnectionState === 'Connected' && tenantCode && userId) {
      try {
        this.sendMessage({
          OperationName: 'UpdateInquery',
          CallRecordId: callRecordId,
        });
      } catch (err: any) {
        console.error("UpdateInquery" + err.toString());
      }
    }
  }

  static setUserLogEvent = (logEvent: any) => {
    const { userId, tenantCode } = store.getState()?.auth?.authUser;
    const currentConnectionState = this.getConnectionState();
    const eventJsonString = JSON.stringify(logEvent);

    if (currentConnectionState === 'Connected' && tenantCode && userId) {
      try {
        appDebugLog("SetUserLogEvent sent msg:" + eventJsonString);
        this.sendMessage({
          OperationName: 'SetUserLogEvent',
          EventJsonString: eventJsonString,
        });
      } catch (err: any) {
        console.error("SetUserLogEvent Error: " + err.toString());
      }
    }
  }

  static getCallRecordStatus = (callRecordId: string) => {
    const { userId, tenantCode } = store.getState()?.auth?.authUser;
    const currentConnectionState = this.getConnectionState();
    if (currentConnectionState === 'Connected' && tenantCode && userId) {
      try {
        this.sendMessage({
          OperationName: 'GetCallStatus',
          CallRecordId: callRecordId,
        });
      } catch (err: any) {
        console.error("getCallRecordStatus" + err.toString());
      }
    }
  }

  /**
   * Send message through WebSocket
   */
  static sendMessage = (data: any): boolean => {
    if (RealTimeClient.connection && RealTimeClient.connection.readyState === WebSocket.OPEN) {
      try {
        const message = typeof data === 'string' ? data : JSON.stringify(data);
        RealTimeClient.connection.send(message);
        appDebugLog(LogTitle, 'Message sent:', data);
        return true;
      } catch (error) {
        console.error(LogTitle, 'Error sending message:', error);
        return false;
      }
    } else {
      appDebugWarn(LogTitle, 'Cannot send message - connection not open, queuing message');
      // Queue the message to be sent when connection is re-established
      RealTimeClient.pendingMessagesQueue.push(data);
      return false;
    }
  }

  /**
   * Get connection state
   */
  static getConnectionState = (): string => {
    if (!RealTimeClient.connection) return 'Disconnected';
    
    switch (RealTimeClient.connection.readyState) {
      case WebSocket.CONNECTING: return 'Connecting';
      case WebSocket.OPEN: return 'Connected';
      case WebSocket.CLOSING: return 'Closing';
      case WebSocket.CLOSED: return 'Disconnected';
      default: return 'Unknown';
    }
  }

  /**
   * When the tab/window was in the background, the browser or network may have closed the socket.
   * We keep a single long-lived connection while `readyState === OPEN`; we only open a new socket
   * when the old one is actually gone — not on every focus change.
   *
   * We do **not** stop heartbeats while hidden: the backend uses them to treat the session as alive;
   * pausing would look like the user went offline and can corrupt availability.
   */
  static handlePageVisibilityChange = async () => {
    const pageBecameVisible =
      typeof document.visibilityState !== 'undefined'
        ? document.visibilityState === 'visible'
        : // @ts-ignore legacy hidden-property fallback
          !document[BrowserApiService.Visibility.visibilityParams.hidden];

    if (!pageBecameVisible) {
      return;
    }

    if (!isVoiceTabLeader()) {
      return;
    }

    if (this.getConnectionState() !== 'Disconnected') {
      return;
    }

    if (!NetworkAvailability.isOnline()) {
      this.pauseReconnectUntilOnline('page visible but offline');
      return;
    }

    try {
      await this.reconnectClient();
      appDebugLog(LogTitle, 'Page visible again; WebSocket was down — reconnecting');
    } catch (e) {
      appDebugWarn(LogTitle, 'Visibility reconnect failed', { error: e });
      if (NetworkAvailability.isNetworkError(e) || !NetworkAvailability.isOnline()) {
        this.pauseReconnectUntilOnline('visibility reconnect network error');
      } else {
        this.scheduleReconnect();
      }
    }
  };

  /**
   * Registering the Page Visibility change handlers
   */
  static registerPageVisibilityHandlers = () => {
    if (this.pageVisibilityListenerAttached) {
      return;
    }
    BrowserApiService.Visibility.registerListener(this.handlePageVisibilityChange);
    this.pageVisibilityListenerAttached = true;
  };

  static unregisterPageVisibilityHandlers = () => {
    if (!this.pageVisibilityListenerAttached) {
      return;
    }
    BrowserApiService.Visibility.unregisterListener(this.handlePageVisibilityChange);
    this.pageVisibilityListenerAttached = false;
  };

  /**
   * Send all queued messages
   */
  static sendPendingMessages = () => {
    const currentQueue = [...RealTimeClient.pendingMessagesQueue];

    currentQueue.forEach(message => {
      this.sendMessage(message);
    });

    // A workaround for race conditions
    // Shouldn't happen, but adding it anyway
    RealTimeClient.pendingMessagesQueue = RealTimeClient.pendingMessagesQueue.splice(0, currentQueue.length);
  }

  /**
   * Listens to user notifications.
   */
  static updateUserNotifications = () => {
    // Handled in handleWebSocketMessage
  }

  /**
   * callSummary is a JSON string. The server sometimes escapes apostrophes as \',
   * which JSON.parse rejects. Strip that escape and try once more.
   */
  static parseCallSummary(raw: unknown): { ConversationSummary?: string } | null {
    if (typeof raw !== 'string' || raw.length === 0) return null;
    try {
      return JSON.parse(raw);
    } catch {
      try {
        return JSON.parse(raw.replace(/\\'/g, "'"));
      } catch (error) {
        appDebugWarn(LogTitle, 'Coaching tip callSummary is not valid JSON', { error });
        return null;
      }
    }
  }

  static handleUserNotification = (notification: any, relayed = false) => {
    appDebugLog(LogTitle, "UserNotification: ", { notification, relayed });
    // TODO: Need to Delete Schedule Call if Notification Type is DeletedScheduleCall
    if (notification?.notificationType === 'ScheduledCalls' && notification?.content?.subType === 'CancelScheduledCall') {
      // store.dispatch(deleteScheduleCall(notification?.content?.callEventId));
    } else if (notification?.notificationType === 'CoachingTips' && notification?.content?.callSummary) {
      const parsedCallSummaryData = RealTimeClient.parseCallSummary(notification.content.callSummary);
      const conversationSummary = parsedCallSummaryData?.ConversationSummary;
      const currentCallRecordId = notification?.content?.callRecordId;
      if (!relayed && conversationSummary) {
        SaleforceService.publishObject({
          type: 'Call Summary',
          title: 'Call Summary',
          summary: conversationSummary,
          callRecordId: currentCallRecordId
        }, "rocketphone__RPISTOLWC__c");
      }
      
      const contactDetails = store.getState()?.auth?.lastCallRecord?.contactDetails;
      let calleeName = '';

      if (contactDetails?.firstName === 'Unknown' && contactDetails?.lastName === 'Caller') {
        calleeName = 'Unknown';
      } else if ((!contactDetails?.firstName || contactDetails?.firstName === '' || contactDetails?.firstName === 'Unknown') && contactDetails?.lastName) {
        calleeName = `${contactDetails?.lastName}`;
      } else if (contactDetails?.firstName && (!contactDetails?.lastName || contactDetails?.lastName === '')) {
        calleeName = `${contactDetails?.firstName}`;
      } else if (contactDetails?.firstName && contactDetails?.lastName) {
        calleeName = `${contactDetails?.firstName} ${contactDetails?.lastName}`;
      } else {
        calleeName = 'Unknown';
      }

      const snackbarData = { contactName: calleeName, date: notification?.createdDate };
      store.dispatch(showSnackbar({ snackbarType: SnackbarType.CoachingTip, snackbarData }));
      if (!relayed) {
        CallNotificationService.showCoachingTipNotification(calleeName);
        SaleforceService.getCallHistory();
      }
    } else if (notification?.notificationType === 'Misscall') {
      const callee = store.getState()?.callingContact?.missedCallee;

      if (!relayed) {
        SaleforceService.getCallHistory();
        CallNotificationService.showMissedCallNotification(notification?.content, callee);
      }
      store.dispatch(clearMissedCallCallee());
    }
  }

  /**
   * Listens to the call list updates
   */
  static UpdateContactInfo = () => {
    // Handled in handleWebSocketMessage
  }

  static handleUpdateContactInfo = (contact: any) => {
    const callingContact = store.getState()?.callingContact?.data;
    const contactId = callingContact?.contactId || callingContact?.id;
    appDebugLog(LogTitle, "UpdateContactInfo: ", { contact, callingContact });

    if (callingContact && contactId === contact?.id) {
      store.dispatch(addCallingContact(contact));
    }
  }

  /**
   * Listens to the RCcall Trascript updates
   */
  static RCCallTranscript = () => {
    // Handled in handleWebSocketMessage
  }

  static handleRCTranscription = (transcript: any) => {
    appDebugLog(LogTitle, "RCTranscription: ", { transcript });
    const notificationForCurrentCall = transcript.callRecordId === store.getState()?.callCentre?.currentActiveCallRecordId;

    if (!notificationForCurrentCall && transcript.segment) {
      var TRANSCRIPTMC = "rocketphone__TRANSCRIPTMC__c";
      SaleforceService.publishObject(transcript, TRANSCRIPTMC);
    }
  }

  static RPBookmarkTriggered = () => {
    // Handled in handleWebSocketMessage
  }

  static handleRPBookmarkTriggered = (capturedBookmark: any, relayed = false) => {
    appDebugLog(LogTitle, "RPBookmarkTriggered: ", { capturedBookmark, relayed });
    this.handleBookmarkResponse(capturedBookmark, relayed);
  }

  public static handleBookmarkResponse = (data: any, relayed = false) => {
    appDebugLog(LogTitle, "handleBookmarkResponse data", data);
    
    if (data) {
      
      store.dispatch(addBookmark(data));
      // screen Pop to specifid action with bookmark id as ref
      const { callData: { id: callRecordId, actions: rawActions, bookmarks } } = data;
      appDebugLog(LogTitle, "data actions", rawActions);

      if (bookmarks?.length > 0 && !relayed) {
        const lastBookmark = bookmarks[bookmarks.length - 1];
        let lastBookmarkObject = { rpCallId: callRecordId, bookmarkData: lastBookmark};
        let bookmarkObject = { bookmarkType: 'All', bookmarkData: lastBookmark, rpCallId: callRecordId };
        var BOOKMARKMC = "rocketphone__BOOKMARKMC__c";
        SaleforceService.publishObject(bookmarkObject, BOOKMARKMC);
        SaleforceService.publishObject(lastBookmarkObject, "rocketphone__SAMPLEMC__c");
        appDebugLog(LogTitle, "bookmarkObject ", bookmarkObject);
        appDebugLog(LogTitle, "lastBookmark ", lastBookmark);
      }

      let lastBookmark = bookmarks[bookmarks.length - 1];

      if (rawActions?.length > 0 && lastBookmark.bookmarkTemplate.bookmarkType === 'ActionEngine') {
        const lastElement = rawActions[rawActions.length - 1];
        let isExist = this._capturedBookmark.find(b => b?.id === lastElement.id);
        if (isExist) {
          return;
        }

        if ((lastElement?.actionType === 'Case' || lastElement?.actionType === 'Opportunity' || lastElement?.actionType === 'ScheduleCallback') && !lastElement?.remoteId) {
          const referenceId = `${lastElement.id}|${callRecordId}`;
          const callingContact = store.getState()?.callingContact?.data;
          let contactType = callingContact?.contactType;
          let contactRemoteId = callingContact?.remoteId;

          let defaultValue = {}
          let objectName: string | null = null;

          if (lastElement?.actionType === 'ScheduleCallback') {
            if (contactType === 'Contact' || contactType === 'Lead') {
              objectName = 'Task';
              defaultValue = { "rocketphone__ReferenceId__c": referenceId, WhoId: contactRemoteId }
            } else if (contactType === 'Account') {
              objectName = 'Task';
              defaultValue = { "rocketphone__ReferenceId__c": referenceId, WhatId: contactRemoteId }
            }
          } else {
            if (contactType === 'Contact' || contactType === 'Account') {
              objectName = lastElement?.actionType === 'Case' ? 'Case' : 'Opportunity';
              if (contactType === 'Contact') {
                defaultValue = { "rocketphone__ReferenceId__c": referenceId, ContactId: contactRemoteId }
              } else {
                defaultValue = { "rocketphone__ReferenceId__c": referenceId, AccountId: contactRemoteId }
              }
            }
          }

          var SAMPLEMC = "rocketphone__SAMPLEMC__c";

          if (objectName && contactRemoteId) {
            this._capturedBookmark.push(lastElement);
            const bookmark = bookmarks.find((bm: any) => bm.id === lastElement?.actionTriggerId);
            appDebugLog('bookmark for publishing:', bookmark);
            const snackbarData = { bookmarkType: lastElement?.actionType };
            store.dispatch(showSnackbar({ snackbarType: SnackbarType.Bookmark, snackbarData }));

            if (!relayed) {
              SaleforceService.openObjectCreationModal(objectName, defaultValue);
            }
            // SaleforceService.publishObject(bookmark, SAMPLEMC);
          }
        }
      }
    }
  }

  /**
   * Listens to recording completion
   */
  static recordingAdded = () => {
    // Skip registering to this event if test flag is set
    // if (window.SIGNALR_DISABLE_RECORDING_EVENT) return;
  }

  /**
   * Listens to transcription completion
   */
  static transcriptionAdded = () => {
    // Skip registering to this event if test flag is set
    // if (window.SIGNALR_DISABLE_TRANSCRIPTION_EVENT) return;
  }

  /**
   * Listens to transcript manual bookmarks completion
   */
  static bookmarkTranscriptionsAdded = () => {
    // Skip registering to this event if test flag is set
    // if (window.SIGNALR_DISABLE_MANUAL_BOOKMARK_EVENT) return;
  }

  /**
   * Listens to call status change
   */
  static updateCallStatusChange = () => {
    // Handled in handleWebSocketMessage
  }

  static mirrorAvailabilityLocally(isOnCall: boolean) {
    const statuses = store.getState()?.settings?.tenantSettings?.userStatuses;
    const statusName = isOnCall ? DefaultUserStatuses.OnCall : DefaultUserStatuses.Available;
    const match = statuses?.find((item: { name?: string; id?: string }) => item?.name === statusName);
    if (!match?.id) {
      return;
    }
    store.dispatch(updateUserAvailabilityStatus({
      available: !isOnCall,
      userStatus: { id: match.id, timestamp: new Date().toISOString() },
    }));
  }

  static handleCallStatusChanged = (notification: any, relayed = false) => {
    appDebugLog(LogTitle, "CallStatusChanged: ", { notification, relayed });
    const { id, phone, status, medium, errorMessage } = notification;
    const notificationForCurrentCall = id === store.getState()?.callCentre?.currentActiveCallRecordId;

    if (medium === 'Sim') {
      if (status === CallActivityStatusEnum.ringing) {
        // TODO: Sent LWC event to stream call start processing
        SaleforceService.getContactSearchByPhone(phone)
          .then((result) => {
            let potentialContact = result?.[0];
            if (potentialContact) {
              store.dispatch(addCallingContact(potentialContact));
            }
          })
          .catch((error) => {
            appDebugLog(LogTitle, "getContactSearchByPhone eception: ", error);
          });
      } else if (status === CallActivityStatusEnum.inProgress) {
        if (relayed) {
          this.mirrorAvailabilityLocally(true);
        } else {
          TwilioVoiceService.updateUserAvailableStatus(id, true);
        }
      } else if (status === CallActivityStatusEnum.complete) {
        if (relayed) {
          this.mirrorAvailabilityLocally(false);
        } else {
          TwilioVoiceService.updateUserAvailableStatus(null, false);
        }
        store.dispatch(clearCallingContact())
      }
    }
    else {
      const callsOnHoldData = store.getState()?.callState?.callsOnHold;

      if (status === 'hangup' && callsOnHoldData[id] && notificationForCurrentCall) {
        const isCallDispositionNotMandatory = store.getState()?.settings?.tenantSettings?.tenantCallSettings?.isCallDispositionNotMandatory;

        if (isCallDispositionNotMandatory) {
          store.dispatch(updateCallBarState(CallBarState.IDLE));
        } else {
          store.dispatch(updateCallBarState(CallBarState.POST_CALL));
        }

        if (relayed) {
          this.mirrorAvailabilityLocally(false);
        } else {
          TwilioVoiceService.updateUserAvailableStatus(null, false);
        }
        store.dispatch(setIncomingCallIdle());
        store.dispatch(updateCurrentActiveCallRecordId(null));
        store.dispatch(setIsCurrentCallInHold(false));
      } else if (status === 'failed') {
        const snackbarData = { message: errorMessage };
        store.dispatch(showSnackbar({ snackbarType: SnackbarType.Error, snackbarData }));
      }
    }
  }

  /**
   * Listens to conference call status change
   */
  static updateConferenceCallStatusChange = () => {
    // Handled in handleWebSocketMessage
  }

  static availabilityInquireEvent = () => {
    // Handled in handleWebSocketMessage
  }

  static handleAvailabilityInquire = () => {
    appDebugLog(LogTitle, "AvailabilityInquire: ");
    TwilioVoiceService.attemptReRegistration();
    this.sendActiveConnectionIds();
  }

  /*
  Listen User Status changed
  */
  static updateUserStatusChange = () => {
    // Handled in handleWebSocketMessage
  }

  static handleUserStatusUpdate = (notification: any) => {
    const userId = store.getState()?.auth?.user?.id;
    appDebugLog(LogTitle, "UserStatusUpdate: ", { notification, userId });
    const userStatus = { id: notification?.userStatusId, timestamp: notification?.lastActiveTime };
    const available = notification?.available;

    if (userId && userId === notification?.userId) {
      store.dispatch(updateUserAvailabilityStatus({ available, userStatus }));
    }
  }

  static updateUserDisconnectMessage = () => {
    // Handled in handleWebSocketMessage
  }

  static handleUserDisconnectMessage = (notification: any) => {
    appDebugLog(LogTitle, "UserDisconnectMessage: ", { notification });
    // const userId = store.getState()?.auth?.user?.id;

    // if (notification?.userId === userId) {
    //   this.sendActiveConnectionIds();
    // }
  }
}
